import React from 'react';
import { Search, Filter, RotateCcw, Settings, Grid3X3, List, LayoutGrid } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

interface KanbanToolbarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  statusFilter: string | null;
  onStatusFilterChange: (value: string | null) => void;
  totalClients: number;
  viewMode: 'comfortable' | 'compact' | 'spacious';
  onViewModeChange: (mode: 'comfortable' | 'compact' | 'spacious') => void;
  onResetLayout: () => void;
  onSettings: () => void;
}

const statusOptions = [
  { value: 'confirmed', label: 'مؤكد', color: 'bg-green-500' },
  { value: 'waiting', label: 'في الانتظار', color: 'bg-yellow-500' },
  { value: 'in_progress', label: 'قيد التنفيذ', color: 'bg-blue-500' },
  { value: 'waiting_supplier', label: 'انتظار المورد', color: 'bg-orange-500' },
  { value: 'cancelled', label: 'ملغي', color: 'bg-red-500' },
  { value: 'pricing_requested', label: 'طلب تسعير', color: 'bg-purple-500' },
  { value: 'pricing_sent', label: 'تم إرسال التسعير', color: 'bg-indigo-500' },
  { value: 'booking_confirmed', label: 'تأكيد الحجز', color: 'bg-teal-500' },
  { value: 'traveling', label: 'في السفر', color: 'bg-cyan-500' },
  { value: 'customer_service', label: 'خدمة العملاء', color: 'bg-pink-500' },
];

export const KanbanToolbar: React.FC<KanbanToolbarProps> = ({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  totalClients,
  viewMode,
  onViewModeChange,
  onResetLayout,
  onSettings,
}) => {
  return (
    <Card className="m-4 p-4 border-none shadow-lg bg-card/50 backdrop-blur-sm">
      <div className="flex flex-col gap-4">
        {/* Top Row */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="البحث عن العملاء..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-9 bg-background/50"
              />
            </div>
            
            <Select value={statusFilter || 'all'} onValueChange={(value) => onStatusFilterChange(value === 'all' ? null : value)}>
              <SelectTrigger className="w-48 bg-background/50">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="تصفية بالحالة" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الحالات</SelectItem>
                {statusOptions.map((status) => (
                  <SelectItem key={status.value} value={status.value}>
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${status.color}`} />
                      {status.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-sm">
              إجمالي العملاء: {totalClients}
            </Badge>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          {/* View Mode Controls */}
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-muted-foreground">طريقة العرض:</span>
            <ToggleGroup 
              type="single" 
              value={viewMode} 
              onValueChange={(value) => value && onViewModeChange(value as 'comfortable' | 'compact' | 'spacious')}
              className="border rounded-md p-1"
            >
              <ToggleGroupItem value="compact" aria-label="عرض مضغوط" size="sm">
                <List className="h-4 w-4" />
                <span className="hidden sm:inline ml-2">مضغوط</span>
              </ToggleGroupItem>
              <ToggleGroupItem value="comfortable" aria-label="عرض مريح" size="sm">
                <LayoutGrid className="h-4 w-4" />
                <span className="hidden sm:inline ml-2">مريح</span>
              </ToggleGroupItem>
              <ToggleGroupItem value="spacious" aria-label="عرض واسع" size="sm">
                <Grid3X3 className="h-4 w-4" />
                <span className="hidden sm:inline ml-2">واسع</span>
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onResetLayout}>
              <RotateCcw className="h-4 w-4 mr-2" />
              إعادة ترتيب
            </Button>
            
            <Separator orientation="vertical" className="h-6" />
            
            <Button variant="outline" size="sm" onClick={onSettings}>
              <Settings className="h-4 w-4 mr-2" />
              إعدادات
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};